import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaPhoneAlt, FaMapMarkerAlt, FaEnvelope, FaExternalLinkAlt, FaWhatsapp } from 'react-icons/fa';
import Footer from './Footer';
import LoaderComponent from './Loader';
import { store } from '../App';
import { apiUrl } from './Constants';
import '../css/Contact.css';

const getPublicAssetBase = () => {
  if (apiUrl && /^https?:\/\//i.test(apiUrl)) {
    try {
      return `${new URL(apiUrl).origin}/public`;
    } catch (e) {
      // fall through
    }
  }
  return 'https://dev10.sbagh.com/public';
};

const getSeoImageUrl = (img) => {
  if (!img || typeof img !== 'string') return null;
  const trimmed = img.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('/')) {
    return trimmed;
  }
  return `${getPublicAssetBase()}/images/seo/large/${trimmed}`;
};

/** Strip CMS editor tags for plain-text uses (map query, etc.) */
const stripHtml = (html) => {
  if (!html || typeof html !== 'string') return '';
  return html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const looksLikeHtml = (value) =>
  typeof value === 'string' && /<\/?[a-z][\s\S]*>/i.test(value);

const isLocalHost = () =>
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const contactPageUrls = () => {
  const remote = `${apiUrl || 'https://dev10.sbagh.com/api/'}getContactUsPageDataBySlug/`;
  const local = 'http://localhost/foton-oman-backend/api/getContactUsPageDataBySlug/';
  const slugs = ['contact', 'contact-us'];
  const bases = isLocalHost() ? [remote, local] : [remote];
  return bases.flatMap((base) => slugs.map((slug) => `${base}${slug}`));
};

const isIconValue = (value) => {
  const icon = String(value || '').trim();
  if (!icon) return false;
  if (/^https?:\/\//i.test(icon) || icon.startsWith('/') || icon.startsWith('data:')) return true;
  if (/\.(svg|png|jpe?g|webp|gif)$/i.test(icon)) return true;
  return /^(fa[a-z-]?|fab|fas|far|fal|fad|bi|ri|icon)(\s|$)|fa-/i.test(icon);
};

const resolveIconSrc = (value) => {
  const icon = String(value || '').trim();
  if (/^https?:\/\//i.test(icon) || icon.startsWith('/') || icon.startsWith('data:')) return icon;
  return `${getPublicAssetBase()}/images/office-locations/large/${icon}`;
};

const buildWhatsAppUrl = (number, shareText) => {
  const digits = String(number || '').replace(/[^\d]/g, '');
  if (!digits) return null;
  const text = String(shareText || '').trim();
  if (!text) return `https://wa.me/${digits}`;
  return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
};

const LocationIcon = ({ value, fallback }) => {
  const icon = String(value || '').trim();
  if (!isIconValue(icon)) return fallback;
  if (/^https?:\/\//i.test(icon) || icon.startsWith('/') || icon.startsWith('data:') || /\.(svg|png|jpe?g|webp|gif)$/i.test(icon)) {
    return <img src={resolveIconSrc(icon)} alt="" />;
  }
  return <i className={icon} aria-hidden="true" />;
};

const isMapEnabled = (location) => {
  if (!location) return false;
  // map_link_status=0 disables map embed + "View on Map" link
  if (location.map_link_status !== undefined && Number(location.map_link_status) === 0) {
    return false;
  }
  return true;
};

const buildMapEmbedUrl = (location, lang) => {
  if (!location || !isMapEnabled(location)) return null;
  const raw = lang
    ? (location.address_ar || location.address_en || '')
    : (location.address_en || location.address_ar || '');
  const address = stripHtml(raw);
  if (address) {
    return `https://maps.google.com/maps?q=${encodeURIComponent(address)}&output=embed&hl=${lang ? 'ar' : 'en'}`;
  }
  return null;
};

function Contact() {
  const [lang] = useContext(store);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [seo, setSeo] = useState(null);
  const [locations, setLocations] = useState([]);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    message: '',
    subject: '',
  });

  useEffect(() => {
    let mounted = true;
    const applyContact = (data) => {
      if (!mounted) return false;
      setSeo(data?.seo || null);
      const rawLocations = data?.contact_us?.locations;
      const active = (Array.isArray(rawLocations) ? rawLocations : [])
        .filter((loc) => loc && (loc.status === undefined || Number(loc.status) === 1))
        .sort((a, b) => (a.sort_order ?? 99) - (b.sort_order ?? 99));
      if (active.length === 0) return false;
      setLocations(active);
      return true;
    };

    setLoading(true);
    const urls = contactPageUrls();
    const loadFrom = (index) => {
      if (!mounted) return;
      if (index >= urls.length) {
        setLocations([]);
        setLoading(false);
        return;
      }
      axios
        .get(urls[index])
        .then((res) => {
          if (!mounted) return;
          const data = res?.data?.success ? res.data.data : null;
          if (data && applyContact(data)) {
            setLoading(false);
            return;
          }
          loadFrom(index + 1);
        })
        .catch(() => loadFrom(index + 1));
    };
    loadFrom(0);

    return () => {
      mounted = false;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const base = apiUrl || 'https://dev10.sbagh.com/api/';
    setSubmitting(true);

    axios
      .post(`${base}saveTheContactUsDetails`, formData)
      .then(() => {
        toast.success(
          lang
            ? 'تم إرسال طلبك. سنتواصل معك قريباً'
            : 'Your request has been submitted. We will contact you soon.'
        );
        setFormData({
          first_name: '',
          last_name: '',
          email: '',
          phone: '',
          message: '',
          subject: '',
        });
      })
      .catch((error) => {
        const response = error?.response?.data?.data;
        if (response && typeof response === 'object') {
          Object.keys(response).forEach((field) => {
            const messages = response[field];
            if (Array.isArray(messages)) {
              messages.forEach((msg) => toast.error(String(msg)));
            }
          });
        } else {
          toast.error(lang ? 'تعذر إرسال النموذج. حاول مرة أخرى.' : 'Could not submit the form. Please try again.');
        }
      })
      .finally(() => setSubmitting(false));
  };

  const heroImage = getSeoImageUrl(seo?.image) ||
    'https://dev10.sbagh.com/public/images/gallery/large/1698822841-Foton-Family-2560-x-1395%20(1).jpg';

  const pageTitle = lang
    ? (seo?.og_title || seo?.meta_title || 'اتصل بنا | فوتون عمان')
    : (seo?.meta_title || seo?.og_title || 'Contact Us | Foton Oman');

  const heroHeading = lang ? 'اتصل بنا' : 'Contact Us';
  const heroDesc = lang
    ? 'تواصل مع فريق فوتون في عُمان للاستفسارات، الدعم، أو تحديد موعد زيارة.'
    : 'Reach the Foton Oman team for enquiries, support, or to arrange a visit.';

  const primaryLocation = locations[0] || null;
  const mapEmbedUrl = useMemo(
    () => buildMapEmbedUrl(primaryLocation, lang),
    [primaryLocation, lang]
  );

  if (loading) {
    return (
      <>
        <LoaderComponent />
        <Footer />
      </>
    );
  }

  return (
    <div className={`cu-page ${lang ? 'cu-rtl' : 'cu-ltr'}`}>
      <Helmet>
        <title>{pageTitle}</title>
        {seo?.meta_description && <meta name="description" content={seo.meta_description} />}
        {seo?.meta_keywords && <meta name="keywords" content={seo.meta_keywords} />}
        {seo?.canonical && <link rel="canonical" href={seo.canonical} />}
        {seo?.robots && <meta name="robots" content={seo.robots} />}
        {seo?.og_title && <meta property="og:title" content={seo.og_title} />}
        {seo?.og_description && <meta property="og:description" content={seo.og_description} />}
        {seo?.og_url && <meta property="og:url" content={seo.og_url} />}
      </Helmet>

      <section
        className="cu-hero"
        style={{ backgroundImage: `url('${heroImage}')` }}
      >
        <div className="cu-hero-overlay" />
        <div className="cu-hero-inner">
          <div className="cu-breadcrumb">
            <Link to="/">{lang ? 'الرئيسية' : 'HOME'}</Link>
            <span>{lang ? '<' : '/'}</span>
            <span>{lang ? 'اتصل بنا' : 'CONTACT US'}</span>
          </div>
          <h1 className="cu-hero-title">{heroHeading}</h1>
          <p className="cu-hero-desc">{heroDesc}</p>
        </div>
      </section>

      <div className="cu-main">
        <div className="cu-grid">
          <section className="cu-panel" data-aos="fade-up">
            <h2 className="cu-panel-title">
              {lang ? 'أرسل رسالة' : 'Send a Message'}
            </h2>
            <p className="cu-panel-sub">
              {lang
                ? 'املأ النموذج وسيتواصل معك فريقنا في أقرب وقت.'
                : 'Fill in the form and our team will get back to you shortly.'}
            </p>

            <form onSubmit={handleSubmit} className="cu-form" noValidate>
              <div className="cu-form-row">
                <div className="cu-field">
                  <input
                    className="cu-input"
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    placeholder={lang ? 'الاسم الأول *' : 'First Name *'}
                    required
                  />
                </div>
                <div className="cu-field">
                  <input
                    className="cu-input"
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    placeholder={lang ? 'اسم العائلة' : 'Last Name'}
                  />
                </div>
                <div className="cu-field">
                  <input
                    className="cu-input"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={lang ? 'البريد الإلكتروني *' : 'Email *'}
                    required
                  />
                </div>
                <div className="cu-field">
                  <input
                    className="cu-input"
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={lang ? 'رقم التواصل *' : 'Contact Number *'}
                    required
                  />
                </div>
                <div className="cu-field cu-field--full">
                  <input
                    className="cu-input"
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder={lang ? 'الموضوع' : 'Subject'}
                  />
                </div>
                <div className="cu-field cu-field--full">
                  <textarea
                    className="cu-textarea"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder={lang ? 'رسالتك' : 'Your Message'}
                    rows={5}
                  />
                </div>
                <div className="cu-field cu-field--full">
                  <button className="cu-submit" type="submit" disabled={submitting}>
                    {submitting
                      ? (lang ? 'جارٍ الإرسال...' : 'Submitting...')
                      : (lang ? 'إرسال' : 'Submit')}
                  </button>
                </div>
              </div>
            </form>
          </section>

          <aside className="cu-panel" data-aos="fade-up" data-aos-delay="120">
            <h2 className="cu-panel-title">
              {lang ? 'معلومات التواصل' : 'Contact Info'}
            </h2>
            {/* <p className="cu-panel-sub">
              {lang
                ? 'تفاصيل المواقع وأرقام التواصل من نظام المحتوى.'
                : 'Location and contact details from the CMS.'}
            </p> */}

            <div className="cu-info-list">
              {locations.length === 0 && (
                <p className="cu-info-text">
                  {lang ? 'لا توجد بيانات تواصل.' : 'No contact details available.'}
                </p>
              )}
              {locations.map((loc, idx) => {
                const addressHtml = lang
                  ? (loc.address_ar || loc.address_en || '')
                  : (loc.address_en || loc.address_ar || '');
                const addressPlain = stripHtml(addressHtml);
                const phone = (loc.contact_number || '').trim();
                const email = (loc.email || '').trim();
                const mapLink = (loc.map_link || '').trim();
                const showMapLink = Boolean(mapLink) && isMapEnabled(loc);
                const phoneHref = phone.replace(/[^\d+]/g, '');
                const contactIcon = (loc.contact_icon || '').trim();
                const whatsappIcon = (loc.whatsapp_icon || '').trim();
                const whatsappNumber = (loc.whatsapp_number || '').trim();
                const whatsappShare = (loc.whatsapp_share_text || '').trim();
                const whatsappUrl = buildWhatsAppUrl(whatsappNumber, whatsappShare);
                const showWhatsapp = Boolean(whatsappNumber || whatsappShare || isIconValue(whatsappIcon));

                return (
                  <div className="cu-location-card" key={loc.id || idx}>
                    {locations.length > 1 && (
                      <span className="cu-location-label">
                        {lang ? `الموقع ${idx + 1}` : `Location ${idx + 1}`}
                      </span>
                    )}

                    {addressPlain && (
                      <div className="cu-info-item">
                        <span className="cu-info-icon"><FaMapMarkerAlt /></span>
                        <div>
                          {looksLikeHtml(addressHtml) ? (
                            <div
                              className="cu-info-text cu-info-html"
                              dangerouslySetInnerHTML={{ __html: addressHtml }}
                            />
                          ) : (
                            <p className="cu-info-text">{addressHtml}</p>
                          )}
                          {showMapLink && (
                            <a
                              className="cu-map-link"
                              href={mapLink}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {lang ? 'عرض على الخريطة' : 'View on Map'}
                              <FaExternalLinkAlt size={11} />
                            </a>
                          )}
                        </div>
                      </div>
                    )}

                    {phone && (
                      <div className="cu-info-item">
                        <span className="cu-info-icon">
                          <LocationIcon value={contactIcon} fallback={<FaPhoneAlt />} />
                        </span>
                        <p className="cu-info-text">
                          <a href={`tel:${phoneHref}`}>{phone}</a>
                        </p>
                      </div>
                    )}

                    {showWhatsapp && (
                      <div className="cu-info-item">
                        <span className="cu-info-icon cu-info-icon--whatsapp">
                          <LocationIcon value={whatsappIcon} fallback={<FaWhatsapp />} />
                        </span>
                        <p className="cu-info-text">
                          {whatsappUrl ? (
                            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                              {whatsappNumber || whatsappShare}
                            </a>
                          ) : (
                            <span>{whatsappNumber || whatsappShare}</span>
                          )}
                          {whatsappShare && whatsappShare !== whatsappNumber ? (
                            <span className="cu-info-sub">{whatsappShare}</span>
                          ) : null}
                        </p>
                      </div>
                    )}

                    {email && (
                      <div className="cu-info-item">
                        <span className="cu-info-icon"><FaEnvelope /></span>
                        <p className="cu-info-text">
                          <a href={`mailto:${email}`}>{email}</a>
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </aside>
        </div>

        {mapEmbedUrl && (
          <section className="cu-map-section" data-aos="fade-up">
            <iframe
              className="cu-map-frame"
              title={lang ? 'خريطة الموقع' : 'Location map'}
              src={mapEmbedUrl}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </section>
        )}
      </div>

      <Footer />
    </div>
  );
}

export default Contact;
